import { useState } from 'react';
import { Plus, Clock, Flame, Eye } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useCart } from '../../context/CartContext';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

const tagLabels = {
  vegetarian: { label: 'V', title: 'Vegetariano' },
  vegan: { label: 'VG', title: 'Vegano' },
  'gluten-free': { label: 'GF', title: 'Sin Gluten' },
  spicy: { label: '🌶', title: 'Picante' },
};

export function ProductCard({ product, onViewDetails, index = 0 }) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const { addItem, openCart } = useCart();

  const handleQuickAdd = (e) => {
    e.stopPropagation();
    
    // If product has variants or extras, open details instead
    if ((product.variants && product.variants.length > 0) || (product.extras && product.extras.length > 0)) {
      onViewDetails(product);
      return;
    }
    
    addItem({
      ...product,
      quantity: 1,
      selectedVariants: {},
      selectedExtras: [],
      note: '',
    });
    
    toast.success(`${product.name} agregado`, {
      description: 'Revisa tu carrito para continuar',
      action: {
        label: 'Ver carrito',
        onClick: () => openCart(),
      },
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
      className="group relative bg-card border border-border/40 overflow-hidden transition-all duration-300 hover:shadow-lg hover:-translate-y-1 cursor-pointer"
      onClick={() => onViewDetails(product)}
      data-testid={`product-card-${product.id}`}
    >
      {/* Image */}
      <div className="relative aspect-[4/3] overflow-hidden bg-secondary">
        {!imageLoaded && (
          <div className="absolute inset-0 animate-pulse bg-muted" />
        )}
        <img
          src={product.image}
          alt={product.name}
          className={`w-full h-full object-cover transition-all duration-500 group-hover:scale-105 ${
            imageLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          onLoad={() => setImageLoaded(true)}
          loading="lazy"
        />
        
        {/* Tags overlay */}
        {product.tags && product.tags.length > 0 && (
          <div className="absolute top-3 left-3 flex gap-1.5">
            {product.tags.map(tag => (
              <span
                key={tag}
                className="bg-background/90 backdrop-blur-sm text-xs font-medium px-2 py-1 rounded-full"
                title={tagLabels[tag]?.title}
              >
                {tagLabels[tag]?.label}
              </span>
            ))}
          </div>
        )}
        
        {/* Popular badge */}
        {product.isPopular && (
          <div className="absolute top-3 right-3">
            <Badge className="bg-accent text-accent-foreground">Popular</Badge>
          </div>
        )}

        {/* Hover overlay */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
          <span className="bg-white text-black text-sm font-medium px-4 py-2 rounded-full flex items-center gap-2 transform scale-90 group-hover:scale-100 transition-transform">
            <Eye className="w-4 h-4" />
            Ver detalles
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <h3 className="font-medium text-base leading-tight truncate">{product.name}</h3>
            <p className="text-sm text-muted-foreground mt-1 line-clamp-2 leading-relaxed">
              {product.description}
            </p>
          </div>
        </div>

        {/* Meta info */}
        <div className="flex items-center gap-3 mt-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" />
            {product.prepTime} min
          </span>
          {product.spiceLevel && (
            <span className="flex items-center gap-1">
              <Flame className="w-3.5 h-3.5 text-orange-500" />
              {Array(product.spiceLevel).fill('🌶').join('')}
            </span>
          )}
          {product.calories && (
            <span>{product.calories} kcal</span>
          )}
        </div>

        {/* Price and Add button */}
        <div className="flex items-center justify-between mt-4">
          <span className="text-lg font-semibold">${product.price}</span>
          <Button
            size="sm"
            onClick={handleQuickAdd}
            className="h-9 px-4 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-all active:scale-95"
            data-testid={`add-to-cart-${product.id}`}
          >
            <Plus className="w-4 h-4 mr-1" />
            Agregar
          </Button>
        </div>
      </div>
    </motion.div>
  );
}

export function ProductCardSkeleton() {
  return (
    <div className="bg-card border border-border/40 overflow-hidden animate-pulse">
      <div className="aspect-[4/3] bg-muted" />
      <div className="p-4 space-y-3">
        <div className="h-5 bg-muted rounded w-3/4" />
        <div className="h-4 bg-muted rounded w-full" />
        <div className="h-4 bg-muted rounded w-2/3" />
        <div className="flex justify-between items-center mt-4">
          <div className="h-6 bg-muted rounded w-16" />
          <div className="h-9 bg-muted rounded-full w-24" />
        </div>
      </div>
    </div>
  );
}
